/**
 * Native push notifications (iOS APNs via Capacitor).
 *
 * Nothing is sent automatically. Registration only happens when the member
 * chooses to turn reminders on, and the device token is stored so we can
 * later send live-session reminders, new content, announcements and gentle
 * wellbeing reminders when those are explicitly configured.
 */

import { isJournalNativeApp, isNativeApp, nativePlatform } from "./native-app";
import { registerPushToken, removePushToken } from "./push-tokens.functions";

export type PushPermission = "granted" | "denied" | "prompt" | "unsupported";

export async function nativePushSupported(): Promise<boolean> {
  return isNativeApp();
}

export async function checkNativePushPermission(): Promise<PushPermission> {
  if (!isNativeApp()) return "unsupported";
  try {
    const status = isJournalNativeApp()
      ? await (await import("@capacitor-firebase/messaging")).FirebaseMessaging.checkPermissions()
      : await (await import("@capacitor/push-notifications")).PushNotifications.checkPermissions();
    if (status.receive === "granted") return "granted";
    if (status.receive === "denied") return "denied";
    return "prompt";
  } catch {
    return "unsupported";
  }
}

/** Asks for permission, registers with APNs, and stores the token. */
export async function enableNativePush(): Promise<{ ok: boolean; reason?: string }> {
  if (!isNativeApp()) return { ok: false, reason: "unsupported" };
  if (isJournalNativeApp()) {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const permission = await FirebaseMessaging.requestPermissions();
    if (permission.receive !== "granted") return { ok: false, reason: "denied" };
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return { ok: false, reason: "no-token" };
    await registerPushToken({ data: { token, platform: nativePlatform() } });
    return { ok: true };
  }
  const { PushNotifications } = await import("@capacitor/push-notifications");

  const permission = await PushNotifications.requestPermissions();
  if (permission.receive !== "granted") return { ok: false, reason: "denied" };

  const token = await new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (value: string | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    void PushNotifications.addListener("registration", (t) => finish(t.value));
    void PushNotifications.addListener("registrationError", () => finish(null));
    void PushNotifications.register();
    setTimeout(() => finish(null), 15000);
  });

  if (!token) return { ok: false, reason: "no-token" };

  await registerPushToken({ data: { token, platform: nativePlatform() } });
  return { ok: true };
}

export async function disableNativePush(): Promise<void> {
  if (!isNativeApp()) return;
  try {
    if (isJournalNativeApp()) {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      await FirebaseMessaging.deleteToken();
      await FirebaseMessaging.removeAllDeliveredNotifications();
      await removePushToken();
      return;
    }
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.removeAllListeners();
    const delivered = await PushNotifications.getDeliveredNotifications();
    if (delivered.notifications.length) {
      await PushNotifications.removeDeliveredNotifications(delivered);
    }
  } catch {
    /* ignore */
  }
  await removePushToken();
}

/** Routes a tapped notification to the right screen. */
export async function installPushTapHandler(navigate: (path: string) => void): Promise<void> {
  if (!isNativeApp()) return;
  try {
    if (isJournalNativeApp()) {
      const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
      await FirebaseMessaging.addListener("notificationActionPerformed", (action) => {
        const payload = action.notification.data;
        const path = payload && typeof payload === "object" && "url" in payload
          ? (payload as { url?: unknown }).url
          : undefined;
        if (typeof path === "string" && path.startsWith("/")) navigate(path);
      });
      return;
    }
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
      const path = (action.notification.data as { url?: string } | undefined)?.url;
      if (typeof path === "string" && path.startsWith("/")) navigate(path);
    });
  } catch {
    /* ignore */
  }
}
